import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';

/**
 * Страница «Права администраторов» — CRUD-список пресетов прав.
 *
 * Layout как у /settings или /email-templates: слева список пресетов,
 * справа редактор. URL хранит ?edit=<id>|new. Применение пресета к
 * конкретному сотруднику — отдельно, в StaffEditModal (там кнопка
 * «Применить пресет»).
 *
 * UI прав идентичен матрице в StaffEditModal — те же группы, те же
 * чекбоксы read/write/delete/trigger/control. Источник — реестр
 * `/api/staff/permissions-registry`, тот же что и в карточке сотрудника.
 */

const ACTION_LABELS = {
  read:    'Чтение',
  write:   'Запись',
  delete:  'Удаление',
  trigger: 'Запуск',
  control: 'Управление',
};

const URL_SCHEMA = { edit: '' };

export default function PermissionPresetsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canRead   = can('permission_presets', 'read');
  const canWrite  = can('permission_presets', 'write');
  const canDelete = can('permission_presets', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [list, setList]           = useState([]);
  const [listLoading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // Реестр прав — общий с карточкой сотрудника. Грузим один раз для всей
  // страницы.
  const [registry, setRegistry] = useState(null);
  useEffect(() => {
    api.get('/api/staff/permissions-registry')
      .then((res) => setRegistry(res.registry || {}))
      .catch(() => setRegistry({}));
  }, []);

  const reloadList = () => {
    setLoading(true); setListError(null);
    api.get('/api/permission-presets')
      .then((res) => setList(res.rows || []))
      .catch((e) => setListError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reloadList, []);

  if (!canRead) {
    return (
      <>
        <Topbar
          breadcrumb={['Администрирование', 'Права администраторов']}
          density={tweaks.density}
          onDensityChange={(d) => updateTweak('density', d)}
        />
        <div className="ref-empty" style={{ padding: 32 }}>Нет доступа.</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        breadcrumb={['Администрирование', 'Права администраторов']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="settings-page">
        <aside className="settings-nav">
          <div className="settings-nav__title">Пресеты</div>
          {canWrite && (
            <button
              type="button"
              className={'settings-nav__item' + (params.edit === 'new' ? ' is-active' : '')}
              onClick={() => setParams({ edit: 'new' })}
              style={{ textAlign: 'left' }}
            >
              <Icon name="plus" size={11} /> Новый пресет
            </button>
          )}
          {listLoading && <div className="settings-nav__item">Загрузка…</div>}
          {listError && <div className="settings-nav__item" style={{ color: 'var(--red-fg)' }}>Ошибка</div>}
          {list.map((p) => (
            <button
              key={p.id}
              type="button"
              className={'settings-nav__item' + (String(params.edit) === String(p.id) ? ' is-active' : '')}
              onClick={() => setParams({ edit: String(p.id) })}
              title={p.description}
              style={{ textAlign: 'left' }}
            >
              {p.name}
              <span className="settings-nav__lock" style={{ background: 'var(--panel-2)', color: 'var(--fg-muted)' }}>
                {p.isSuperAdmin ? 'super' : (p.permissionsCount + ' прав')}
              </span>
            </button>
          ))}
        </aside>
        <div className="settings-main">
          {params.edit === 'new' && registry && (
            <PresetEditor
              key="new"
              preset={null}
              registry={registry}
              canWrite={canWrite}
              canDelete={false}
              onSaved={(saved) => {
                reloadList();
                setParams({ edit: String(saved.id) });
              }}
              onDeleted={() => {}}
            />
          )}
          {params.edit && params.edit !== 'new' && registry && (
            <PresetEditor
              key={params.edit}
              presetId={Number(params.edit)}
              registry={registry}
              canWrite={canWrite}
              canDelete={canDelete}
              onSaved={() => reloadList()}
              onDeleted={() => { setParams({ edit: '' }); reloadList(); }}
            />
          )}
          {!params.edit && (
            <div className="settings-card">Выберите пресет слева или создайте новый.</div>
          )}
        </div>
      </div>
    </>
  );
}

function PresetEditor({ presetId, preset, registry, canWrite, canDelete, onSaved, onDeleted }) {
  const isNew = !presetId;
  const [data, setData] = useState({
    name: '', description: '', isSuperAdmin: false, permissions: {},
  });
  const [loading, setLoading] = useState(!isNew);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    setLoading(true); setError(null);
    api.get(`/api/permission-presets/${presetId}`)
      .then((res) => {
        const p = res.preset;
        setData({
          name:         p.name || '',
          description:  p.description || '',
          isSuperAdmin: !!p.isSuperAdmin,
          permissions:  p.permissions || {},
        });
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, [presetId, isNew]);

  // Группируем реестр по group, чтобы UI выглядел так же, как в StaffEditModal.
  const grouped = useMemo(() => {
    const byGroup = new Map();
    for (const [key, meta] of Object.entries(registry || {})) {
      if (!byGroup.has(meta.group)) byGroup.set(meta.group, []);
      byGroup.get(meta.group).push({ key, ...meta });
    }
    return Array.from(byGroup.entries()).map(([group, items]) => ({
      group,
      items: items.sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    }));
  }, [registry]);

  const togglePerm = (key, action) => {
    setData((d) => {
      const cur = d.permissions[key] || [];
      const has = cur.includes(action);
      const next = has ? cur.filter((a) => a !== action) : [...cur, action];
      const newMap = { ...d.permissions };
      if (next.length) newMap[key] = next;
      else delete newMap[key];
      return { ...d, permissions: newMap };
    });
    setSavedAt(null);
  };

  const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setSavedAt(null); };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        name:        data.name,
        description: data.description,
        isSuperAdmin: data.isSuperAdmin,
        permissions: data.permissions,
      };
      const res = isNew
        ? await api.post('/api/permission-presets', payload)
        : await api.put('/api/permission-presets/' + presetId, payload);
      setSavedAt(new Date());
      onSaved && onSaved(res.preset);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setSaving(true); setError(null);
    try {
      await api.delete('/api/permission-presets/' + presetId);
      onDeleted && onDeleted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось удалить');
    } finally {
      setSaving(false);
      setConfirmDel(false);
    }
  };

  if (loading) return <div className="settings-card">Загрузка…</div>;

  return (
    <div className="settings-card">
      <div className="settings-card__head">
        <h2 className="settings-card__title">
          {isNew ? 'Новый пресет' : data.name || 'Пресет'}
        </h2>
        <p className="settings-card__sub">
          Набор прав, который можно одним кликом применить к администратору в карточке сотрудника.
          Применение копирует права в самого пользователя — последующая правка пресета на уже применённых сотрудников не повлияет.
        </p>
      </div>

      <div className="settings-grid">
        <div className="settings-field settings-field--wide">
          <label className="settings-field__label">Название *</label>
          <input
            className="settings-field__input"
            value={data.name}
            onChange={(e) => set('name', e.target.value)}
            readOnly={!canWrite}
            placeholder="Например: Бухгалтер, Завуч, Касса"
          />
        </div>
        <div className="settings-field settings-field--wide">
          <label className="settings-field__label">Описание</label>
          <input
            className="settings-field__input"
            value={data.description}
            onChange={(e) => set('description', e.target.value)}
            readOnly={!canWrite}
            placeholder="Кому подходит этот пресет"
          />
        </div>
        <div className="settings-field settings-field--wide">
          <label className="ref-checkbox" style={{ gap: 8, display: 'inline-flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={!!data.isSuperAdmin}
              onChange={(e) => set('isSuperAdmin', e.target.checked)}
              disabled={!canWrite}
            />
            <span>Супер-администратор (все права автоматически)</span>
          </label>
          <div className="settings-field__hint">
            Если включено — пользователь получает доступ ко всему, кроме блокировок типа замка периода. Отдельные галки прав ниже игнорируются на стороне сервера.
          </div>
        </div>

        <div className="settings-field settings-field--wide">
          <label className="settings-field__label">Права</label>
          <div className="staff-perms">
            {grouped.map(({ group, items }) => (
              <div key={group} className="staff-perms__group">
                <div className="staff-perms__group-title">{group}</div>
                <table className="staff-perms__table">
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.key}>
                        <td className="staff-perms__label">{it.label}</td>
                        {it.actions.map((a) => (
                          <td key={a} className="staff-perms__cell">
                            <label>
                              <input
                                type="checkbox"
                                checked={(data.permissions[it.key] || []).includes(a)}
                                onChange={() => togglePerm(it.key, a)}
                                disabled={!canWrite}
                              />
                              <span>{ACTION_LABELS[a] || a}</span>
                            </label>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="settings-card__foot">
        {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5 }}>{error}</div>}
        {savedAt && !error && (
          <div style={{ color: 'var(--green-700)', fontSize: 12.5 }}>
            Сохранено в {savedAt.toLocaleTimeString('ru-RU')}
          </div>
        )}
        <div className="settings-card__spacer" />
        {!isNew && canDelete && !confirmDel && (
          <button type="button" className="btn btn--sm btn--danger" onClick={() => setConfirmDel(true)} disabled={saving}>
            Удалить
          </button>
        )}
        {!isNew && canDelete && confirmDel && (
          <>
            <span style={{ fontSize: 12.5, color: 'var(--red-fg)' }}>Удалить пресет? Это не откатит уже применённые права у сотрудников.</span>
            <button type="button" className="btn btn--sm" onClick={() => setConfirmDel(false)} disabled={saving}>Отмена</button>
            <button type="button" className="btn btn--sm btn--danger" onClick={del} disabled={saving}>Да, удалить</button>
          </>
        )}
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={save}
          disabled={!canWrite || saving}
        >
          {saving ? 'Сохраняем…' : (isNew ? 'Создать' : 'Сохранить')}
        </button>
      </div>
    </div>
  );
}
